/**
 * Ожидание, пустота, ошибка. Три состояния, которые в измерительном продукте
 * важнее удачного пути: молча выродившееся число хуже видимого пробела.
 *
 * Спиннера в системе нет и он не нужен: у каждой операции есть либо счётчик,
 * либо шаг конвейера.
 */

import type { ReactNode } from 'react';
import { ApiError, humanMessage, problemTitle } from '../api/problem';
import { tx, useT } from '../i18n/react';
import { LEGAL_ENTITY } from '@shared/src/product';

export function Skeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div className="skeleton" key={i} />
      ))}
    </div>
  );
}

/** Фаза без счётчика: показываем шаги конвейера, а не неопределённый индикатор. */
export function Pipeline({ steps, current }: { steps: string[]; current: number }) {
  return (
    <p className="steps" role="status" aria-live="polite">
      {steps.map((step, i) => (
        <span
          key={step}
          className={i < current ? 'done' : i === current ? 'now' : undefined}
        >
          {step}
          {i < steps.length - 1 ? ' →' : ''}
        </span>
      ))}
    </p>
  );
}

/** Фаза со счётчиком: определённая полоса плюс строка настоящих событий. */
export function Progress({
  done,
  total,
  caption,
}: {
  done: number;
  total: number;
  caption: string;
}) {
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div>
      <div
        className="progress"
        role="progressbar"
        aria-valuenow={done}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-label={caption}
      >
        <i style={{ ['--w' as string]: `${percent}%` }} />
      </div>
      <p className="stamp">{caption}</p>
    </div>
  );
}

/** Пустое состояние: надзаголовок, заголовок, одно предложение, одно действие. */
export function Empty({
  eyebrow,
  title,
  children,
  action,
}: {
  eyebrow: string;
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="state">
      <span className="eyebrow">{eyebrow}</span>
      <h2>{title}</h2>
      <p>{children}</p>
      {action}
    </div>
  );
}

/**
 * Ошибка. Текст берётся из проблемы, которую прислал сервер: он знает числа и
 * сроки.
 *
 * Номер запроса показывается **только на наших сбоях** (5xx). Раньше он висел
 * под любым отказом, включая «Такая почта уже занята», — и человек получал
 * тридцать два шестнадцатеричных знака без единого слова о том, что с ними
 * делать. На отказе, который человек исправляет сам, номер запроса не помогает
 * никому; на нашем сбое он единственное, что двигает дело, — поэтому идёт
 * вместе с адресом, по которому его ждут.
 */
export function ErrorNotice({ error, action }: { error: unknown; action?: ReactNode }) {
  const t = useT();
  const problem = error instanceof ApiError ? error.problem : null;
  const title = problemTitle(problem);
  const text = problem ? humanMessage(problem) : t('error.ours');
  // Наш сбой определяется не только кодом ответа: нечитаемый ответ приходит с
  // `200` от того, кто стоит перед сервисом, и несёт `code: 'internal'`. По
  // одному лишь `status >= 500` такой случай просил написать в поддержку и
  // прятал единственное, с чем в поддержку идут.
  const ours = problem !== null && (problem.status >= 500 || problem.code === 'internal');

  return (
    <div className="notice" role="alert">
      <b>{title}</b>
      {text}
      {ours && problem.request_id ? (
        <span className="stamp">
          {/* Фраза собрана одним куском, а не тремя: ссылка и номер запроса
              стоят внутри предложения, и переводчик обязан иметь право
              переставить их — в арабском порядок слов другой. */}
          {tx('error.request-id', {
            contact: LEGAL_ENTITY.contact,
            requestId: problem.request_id,
            mail: (chunks) => <a href={`mailto:${LEGAL_ENTITY.contact}`}>{chunks}</a>,
          })}
        </span>
      ) : null}
      {action}
    </div>
  );
}
